"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.YearnTokenMarketCapService = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const types_1 = require("@shapeshiftoss/types");
const uniqBy_1 = __importDefault(require("lodash/uniqBy"));
const config_1 = require("../config");
const bignumber_1 = require("../utils/bignumber");
const rateLimiters_1 = require("../utils/rateLimiters");
const rateLimiter = (0, rateLimiters_1.createRateLimiter)(config_1.RATE_LIMIT_THRESHOLDS_PER_MINUTE.DEFAULT);
const USDC_PRECISION = 6;
class YearnTokenMarketCapService {
    constructor(args) {
        this.baseUrl = 'https://api.yearn.finance';
        this.defaultGetByMarketCapArgs = {
            count: 2500
        };
        this.findAll = async (args) => {
            try {
                const argsToUse = { ...this.defaultGetByMarketCapArgs, ...args };
                const response = await Promise.allSettled([
                    rateLimiter(() => this.yearnSdk.ironBank.tokens()),
                    rateLimiter(() => this.yearnSdk.tokens.supported()),
                    rateLimiter(() => this.yearnSdk.vaults.tokens())
                ]);
                const [ironBankResponse, zapperResponse, underlyingTokensResponse] = response;
                // Ignore rejected promises, return successful responses.
                const responseTokens = [
                    ...(ironBankResponse.status === 'fulfilled' ? ironBankResponse.value : []),
                    ...(zapperResponse.status === 'fulfilled' ? zapperResponse.value : []),
                    ...(underlyingTokensResponse.status === 'fulfilled' ? underlyingTokensResponse.value : [])
                ];
                const uniqueTokens = (0, uniqBy_1.default)(responseTokens, 'address');
                const tokens = uniqueTokens.slice(0, argsToUse.count);
                return tokens.reduce((acc, token) => {
                    const _assetId = (0, caip_1.toAssetId)({
                        chain: types_1.ChainTypes.Ethereum,
                        network: types_1.NetworkTypes.MAINNET,
                        assetNamespace: 'erc20',
                        assetReference: token.address
                    });
                    acc[_assetId] = {
                        price: (0, bignumber_1.bnOrZero)(token.priceUsdc).div(`1e+${USDC_PRECISION}`).toString(),
                        // TODO: figure out how to get these values.
                        marketCap: '0',
                        volume: '0',
                        changePercent24Hr: 0
                    };
                    return acc;
                }, {});
            }
            catch (e) {
                console.info(e);
                return {};
            }
        };
        this.findByAssetId = async ({ assetId: _assetId }) => {
            const address = caip_1.adapters.assetIdToYearn(_assetId);
            if (!address)
                return null;
            try {
                // the yearnSdk caches the response to all of these calls and returns the cache if found.
                // Unfortunately, the functions do not take in an argument for a single address to return a
                // single token, so we are limited to getting all tokens then doing a find on them to return
                // the price to web. Doing allSettled so that one rejection does not interfere with the other
                // calls.
                const response = await Promise.allSettled([
                    rateLimiter(() => this.yearnSdk.ironBank.tokens()),
                    rateLimiter(() => this.yearnSdk.tokens.supported()),
                    rateLimiter(() => this.yearnSdk.vaults.tokens())
                ]);
                const [ironBankResponse, zapperResponse, underlyingTokensResponse] = response;
                // Ignore rejected promises, return successful responses.
                const responseTokens = [
                    ...(ironBankResponse.status === 'fulfilled' ? ironBankResponse.value : []),
                    ...(zapperResponse.status === 'fulfilled' ? zapperResponse.value : []),
                    ...(underlyingTokensResponse.status === 'fulfilled' ? underlyingTokensResponse.value : [])
                ];
                const token = responseTokens.find((tok) => tok.address === address);
                if (!token)
                    return null;
                return {
                    price: (0, bignumber_1.bnOrZero)(token.priceUsdc).div(`1e+${USDC_PRECISION}`).toString(),
                    marketCap: '0',
                    volume: '0',
                    changePercent24Hr: 0
                };
            }
            catch (e) {
                console.warn(e);
                throw new Error('YearnMarketService(findByAssetId): error fetching market data');
            }
        };
        this.findPriceHistoryByAssetId = async () => {
            // TODO: figure out a way to get zapper, ironbank and underlying token historical data.
            return [];
        };
        this.yearnSdk = args.yearnSdk;
    }
}
exports.YearnTokenMarketCapService = YearnTokenMarketCapService;
